import { useTranslation } from 'react-i18next';
import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
  useInfiniteQuery,
} from 'react-query';
import { useDispatch } from 'react-redux';

import { addSnackbar } from 'theSource/components';
import { api, mapFeedMessages } from 'theSource/utils';
import {
  getNextPage,
  GET_FEED_MESSAGES_QUERY_KEY,
  MAX_NUMBER_OF_MESSAGES_PER_REQUEST,
} from './utils';
import { FeedMessage, FeedMessagesResponseDto } from 'theSource/types';

export interface UseFeedMessagesQueryResult {
  feedMessagesData: InfiniteData<FeedMessage[]> | undefined;
  feedMessages: FeedMessage[] | undefined;
  isLoadingFeedMessages: boolean;
  isFetchingFeedMessages: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined
  ) => Promise<InfiniteQueryObserverResult<FeedMessage[], unknown>>;
  hasNextPage: boolean | undefined;
}

interface UseFeedMessageQueryProps {
  enabled?: boolean;
}

export const useFeedMessagesQuery = ({
  enabled,
}: UseFeedMessageQueryProps): UseFeedMessagesQueryResult => {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const fetchMessages = ({ pageParam = 1 }): Promise<FeedMessage[]> =>
    api
      .get<FeedMessagesResponseDto>(`/feed_messages?page=${pageParam}`)
      .then((response) => mapFeedMessages(response.data));

  const {
    data: feedMessagesData,
    isLoading: isLoadingFeedMessages,
    isFetching: isFetchingFeedMessages,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(GET_FEED_MESSAGES_QUERY_KEY, fetchMessages, {
    enabled: Boolean(enabled),
    getNextPageParam: (lastPage, allPages) =>
      getNextPage(lastPage, allPages, MAX_NUMBER_OF_MESSAGES_PER_REQUEST),
    onError: () =>
      dispatch(
        addSnackbar({
          message: t('serverError')!,
          severity: 'error',
          disableAutoHide: true,
        })
      ),
  });

  const feedMessages = feedMessagesData?.pages.flatMap((page) => page);

  return {
    feedMessagesData,
    feedMessages,
    isLoadingFeedMessages,
    isFetchingFeedMessages,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  };
};
